import { getModifiedItm } from '../api/utils/api-item'
import { responseHasError } from '../stores/users/actions'
import {
	doneLoading,
	startError,
	startLoading
} from '../stores/work-progress/actions'
import { translate } from '../cls/TranslationContext'

/**
 * Checks whether a remote item has been modified compared to a given timestamp.
 *
 * @async
 * @function
 * @param {string|number} modified – The local “modified” timestamp to compare against.
 * @param {string} targetLst – Identifier of the target list or resource to fetch.
 * @param {Object} params – Parameters for the API call.
 * @returns {Promise<boolean>} True if the remote item’s modified timestamp differs, false otherwise.
 */
export const isModifiedItm = async (modified, targetLst, params) => {
	try {
		const result = await getModifiedItm(targetLst, params)

		// result.modified and modified, must be defined and not null
		if (!result?.modified || !modified) return false

		if (result.modified !== modified) {
			return true
		}
	} catch (err) {
		console.error('isModifiedItm error:', err)
	}
	return false
}

/**
 * Wraps an asynchronous function with a loading indicator, error handling, and optional re-throw.
 *
 * @function
 * @param {Function} fn – The async function to wrap.
 * @param {Object} [options={}] – Configuration options.
 * @param {string} [options.loadingMessage] – Custom message to display during loading.
 * @param {string} [options.t] – Translation key or function context for the loading message.
 * @param {boolean} [rethrow=false] – If true, re-throws caught errors after handling.
 * @returns {Function} A new async function that manages loading state and errors around `fn`.
 *
 * @example
 * const fetchDataWithLoading = wrapWithLoading(fetchData, { loadingMessage: 'app.loadingData' })
 * await fetchDataWithLoading(params)
 */
export const wrapWithLoading =
	(fn, options = {}, rethrow = false) =>
	async (...args) => {
		let _progressId

		try {
			const showMessage =
				options?.loadingMessage ??
				translate(options?.t || 'single.loading')

			// Start the loading process
			_progressId = startLoading(showMessage)

			// Execute the wrapped function
			const result = await fn(...args)

			// Execute specific error actions
			const hasError = responseHasError(result)
			// Handle specific authorization error
			if (hasError && result.action === 'logout') {
				startError(err.message)
				// Call the logout reditect
				window.location.href = '/login'
				return false
			}

			// Return the result if no error occurred
			return result
		} catch (err) {
			!rethrow && console.error('wrapWithLoading error:', err)

			// Check if the message looks like a stringified JSON object
			if (
				err.message &&
				typeof err.message === 'string' &&
				(err.message.startsWith('{') || err.message.startsWith('['))
			) {
				try {
					// Attempt to parse the message
					const parsedMessage = JSON.parse(err.message)
					if (
						typeof parsedMessage === 'object' &&
						parsedMessage !== null
					) {
						err = parsedMessage // Replace the error with the parsed object
					}
				} catch (parseError) {
					console.error(
						'Failed to parse err.message as JSON:',
						parseError
					)
				}
			}
			console.error('wrapWithLoading final error:', err)
			// Execute specific error actions
			responseHasError(err)

			// Handle specific authorization error
			if (err.status === 'error') {
				startError(err.message)
				if (err.action === 'logout') window.location.href = '/login'
			} else {
				startError('An unexpected error occurred')
			}

			// Re-throw the error to ensure the calling code is aware of the failure
			if (rethrow) throw err
		} finally {
			// Ensure that doneLoading is called even if the wrapped function or error handling fails
			if (_progressId) {
				doneLoading(_progressId)
			}
		}
	}
